// src/utils/sessionFormUtils.ts
// Shared form handlers for CreateSession and EditSession components

import { Session, Component, Platform } from '../types';

/**
 * Toggle a feature in the session's features array.
 * When a feature is deselected, also removes associated components and their statuses.
 */
export function toggleFeature(
  session: Partial<Session>,
  featureId: string,
  components: Component[]
): Partial<Session> {
  const currentFeatures = session.features || [];
  const newFeatures = currentFeatures.includes(featureId)
    ? currentFeatures.filter(id => id !== featureId)
    : [...currentFeatures, featureId];
  
  // Remove components that belong to deselected features
  const newComponents = (session.components || []).filter(componentId => {
    const component = components.find(c => c.id === componentId);
    return component && newFeatures.includes(component.featureId);
  });
  
  // Remove component statuses for removed components (only if they exist)
  const newComponentStatuses = session.componentStatuses
    ? session.componentStatuses.filter(status => newComponents.includes(status.componentId || ''))
    : undefined;
  
  // Also handle legacy subDependencies for backward compatibility
  const newSubDeps = (session.subDependencies || []).filter(subDepId => {
    const subDep = (components as any[]).find((sd: any) => sd.id === subDepId);
    return subDep && newFeatures.includes((subDep as any).featureId);
  });
  
  const newSubDepStatuses = session.subDepStatuses
    ? session.subDepStatuses.filter(status => newSubDeps.includes(status.subDepId))
    : undefined;
  
  return {
    ...session,
    features: newFeatures,
    components: newComponents,
    ...(newComponentStatuses !== undefined && { componentStatuses: newComponentStatuses }),
    subDependencies: newSubDeps,
    ...(newSubDepStatuses !== undefined && { subDepStatuses: newSubDepStatuses })
  };
}

/**
 * Toggle a sub-dependency in the session's subDependencies array.
 * Also manages subDepStatuses - removes status when deselected, adds default when selected.
 */
export function toggleSubDep(
  session: Partial<Session>,
  subDepId: string
): Partial<Session> {
  const currentSubDeps = session.subDependencies || [];
  const newSubDeps = currentSubDeps.includes(subDepId)
    ? currentSubDeps.filter(id => id !== subDepId)
    : [...currentSubDeps, subDepId];
  
  // Only manage subDepStatuses if they exist in the session
  if (session.subDepStatuses !== undefined) {
    // Remove sub-dep status if sub-dep is deselected
    const newSubDepStatuses = session.subDepStatuses.filter(status => 
      newSubDeps.includes(status.subDepId)
    );
    
    // Add default status for newly selected sub-deps
    const existingSubDepIds = new Set(newSubDepStatuses.map(s => s.subDepId));
    newSubDeps.forEach(id => {
      if (!existingSubDepIds.has(id)) {
        newSubDepStatuses.push({
          subDepId: id,
          tested: false,
          bugged: false
        });
      }
    });
    
    return {
      ...session,
      subDependencies: newSubDeps,
      subDepStatuses: newSubDepStatuses
    };
  }
  
  return {
    ...session,
    subDependencies: newSubDeps
  };
}

/**
 * Toggle a component in the session's components array.
 * Also manages componentStatuses - removes status when deselected, adds default when selected.
 */
export function toggleComponent(
  session: Partial<Session>,
  componentId: string
): Partial<Session> {
  const currentComponents = session.components || [];
  const newComponents = currentComponents.includes(componentId)
    ? currentComponents.filter(id => id !== componentId)
    : [...currentComponents, componentId];
  
  // Only manage componentStatuses if they exist in the session
  if (session.componentStatuses !== undefined) {
    // Remove component status if component is deselected
    const newComponentStatuses = session.componentStatuses.filter(status => 
      status.componentId && newComponents.includes(status.componentId)
    );
    
    // Add default status for newly selected components
    const existingComponentIds = new Set(newComponentStatuses.map(s => s.componentId).filter(Boolean));
    newComponents.forEach(id => {
      if (!existingComponentIds.has(id)) {
        newComponentStatuses.push({
          componentId: id,
          tested: false,
          bugged: false
        });
      }
    });
    
    return {
      ...session,
      components: newComponents,
      componentStatuses: newComponentStatuses
    };
  }
  
  return {
    ...session,
    components: newComponents
  };
}


/**
 * Toggle a platform in the session's platforms array.
 */
export function togglePlatform(
  session: Partial<Session>,
  platform: Platform
): Partial<Session> {
  const currentPlatforms = session.platforms || [];
  const newPlatforms = currentPlatforms.includes(platform)
    ? currentPlatforms.filter(p => p !== platform)
    : [...currentPlatforms, platform];
  
  return {
    ...session,
    platforms: newPlatforms
  };
}

